/* Wonder 2.5.1 — low-power-video.js  (Ellitane performans yamasi, 2026-09-07)
 *
 * SORUN: tema her hosted video'yu `autoplay` + `preload="metadata"` ile basiyordu ve
 * gorunurluk kontrolu YOKTU. Ana sayfada 26 video elemanindan 1'i gorunurken 10 dosya
 * / 22,3 MB iniyordu. Ayrica `video_tag` filtresi HER ZAMAN tek kaynak olarak
 * HD-1080p-7.2Mbps veriyor (Shopify dokumaninda `preload` parametresi YOK, rendition
 * secilemiyor) — 300x400 CSS pikselde 1080p.
 *
 * BU DOSYA UC ISI YAPIYOR (hepsi idempotent, tek yerde):
 *   1) Gorunurluk kapisi — IntersectionObserver, rootMargin "200px 0px"
 *      (slider.js `_lazyInit` ile ayni deger). Gorunurken play(), cikinca pause().
 *   2) `autoplay` sokulur + `preload="none"` — Liquid tarafinda autoplay zaten
 *      kaldirildi; bu, dokunulmamis diger cagri yerleri icin emniyet kemeri.
 *   3) Rendition secimi — hero/PDP galerisi ORIJINAL 1080p kalir; kart/thumbnail
 *      olculerinde HD-720p-4.5Mbps. Ilk play()'den ONCE, preload="none"
 *      oldugu icin 1080p'den tek bayt inmez. Rendition yoksa <source> error'unda
 *      orijinal URL'e geri donulur. Ayni gecerken varsa .m3u8 (HLS) kaynagi atilir —
 *      progressive mp4 ayni kalitede daha ucuz.
 *
 * ponytail: rendition secimi JS'te, cunku dogru kademe render edilmis KUTU BOYUTUNA
 * bagli ve Liquid bunu bilmiyor; ustelik `video_tag` rendition parametresi kabul etmiyor.
 * Tavan: her cagri yerini `media.sources` donen bir snippet'e cevirmek.
 */
(() => {
  const REND = /\.HD-1080p-7\.2Mbps-/;
  const R720 = '.HD-720p-4.5Mbps-';

  // Rendition secimi kutunun EKRANA ORANINA gore: tam-genislik alanlar (hero, PDP galerisi)
  // ORIJINAL 1080p (null = dokunma), kart/thumbnail alanlari 720p. Mutlak esik degil oran kullaniyoruz, cunku mobilde
  // hero (390px) ile kart (300px) mutlak olarak birbirine cok yakin.
  function targetRendition(video) {
    let w = video.getBoundingClientRect().width || video.clientWidth || 0;
    // Slider slide'lari ilk anda 0 genislikte olabilir: en yakin olculebilir ataya bak.
    let el = video.parentElement;
    for (let i = 0; i < 4 && el && !w; i++, el = el.parentElement) w = el.getBoundingClientRect().width || 0;
    if (!w) return null; // olculemedi -> orijinal kalsin (guvenli taraf = kalite)
    return w >= Math.min(window.innerWidth * 0.8, 700) ? null : R720;
  }

  const gated = new WeakSet();      // kapiya baglandi
  const autoGated = new WeakSet();  // markup'ta autoplay vardi -> gorununce oynat
  const prepared = new WeakSet();   // rendition secimi yapildi
  const userPaused = new WeakSet(); // kullanici (ya da baska kod) gorunurken durdurdu
  const ourPause = new WeakSet();   // duraklatan biziz

  // autoplay'i sok, preload'u kapat: video gorunur olana kadar tek bayt inmesin.
  function disarm(video) {
    if (video.hasAttribute('autoplay')) {
      video.removeAttribute('autoplay');
      video.autoplay = false;
    }
    if (video.preload !== 'none') video.preload = 'none';
  }

  // Ilk oynatmadan once kaynagi uygun rendition'a cevir + HLS'i at.
  function prepare(video) {
    if (prepared.has(video)) return;
    prepared.add(video);

    const target = targetRendition(video);
    let changed = false;

    const sources = [...video.querySelectorAll('source')];
    const hasMp4 = sources.some((s) => /\.mp4(\?|$)/.test(s.getAttribute('src') || ''));
    for (const s of sources) {
      const src = s.getAttribute('src') || '';
      // Tek teslimat yolu: mp4 varken HLS'i birakma (ayni kalitede TS konteyneri daha agir).
      if (hasMp4 && /\.m3u8(\?|$)/.test(src)) { s.remove(); changed = true; continue; }
      if (target && REND.test(src)) {
        s.dataset.wtOrig = src;
        s.setAttribute('src', src.replace(REND, target));
        changed = true;
      }
    }
    const own = video.getAttribute('src') || '';
    if (target && REND.test(own)) {
      video.dataset.wtOrig = own;
      video.setAttribute('src', own.replace(REND, target));
      changed = true;
    }
    if (changed) video.load(); // yeni <source> secimi icin sart
  }

  // Hedef rendition yoksa (kaynak video zaten kucukse) orijinale don.
  function guardFallback(video) {
    video.addEventListener('error', (e) => {
      const s = e.target;
      if (s && s.tagName === 'SOURCE' && s.dataset.wtOrig) {
        s.setAttribute('src', s.dataset.wtOrig);
        delete s.dataset.wtOrig;
        video.load();
        if (autoGated.has(video) && !userPaused.has(video)) video.play().catch(() => {});
      }
    }, true); // <source> hatalari bubble etmez -> capture
  }

  function start(video) {
    const p = video.play(); // sarmalanmis play() rendition secimini kendisi yapar

    if (p && typeof p.catch === 'function') p.catch(() => {}); // otomatik oynatma reddi sessiz
  }

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const e of entries) {
          const v = e.target;
          if (e.isIntersecting) {
            if (!userPaused.has(v)) start(v);
          } else if (!v.paused) {
            ourPause.add(v);
            v.pause();
          }
        }
      }, { rootMargin: '200px 0px' })
    : null;

  function gate(video) {
    if (!video || gated.has(video)) return;
    gated.add(video);
    // Liquid tarafinda `autoplay="autoplay"` -> `data-wt-autoplay="true"` olarak isaretlendi:
    // tarayici kendiliginden indirmesin ama kapinin "bu video gorununce oynamali" bilgisi kaybolmasin.
    const wasAutoplay = video.hasAttribute('autoplay') || video.autoplay === true
      || video.dataset.wtAutoplay === 'true';
    disarm(video);
    guardFallback(video);
    // Temanin baska script'leri (video-reels.js, slider.js) videoyu dogrudan play() ile
    // baslatiyor ve o yol kapiyi atlayip 1080p indiriyordu. play()'i ELEMAN BAZINDA
    // sarmalayarak her cagirani rendition secimden gecir. (Prototip degistirilmiyor.)
    // NOT: rendition secimi ISTEYEREK play() anina birakildi — `video.load()` cagrisi
    // preload="none" olsa bile Chrome'da veri cekiyor; kapi aninda yapmak ~5 MB geri getirdi.
    const nativePlay = video.play.bind(video);
    video.play = function () { prepare(video); return nativePlay(); };
    // Gorunurken gelen pause'u "kullanici durdurdu" say; kendi pause'umuzu sayma.
    video.addEventListener('pause', () => {
      if (ourPause.has(video)) ourPause.delete(video);
      else userPaused.add(video);
    });
    video.addEventListener('play', () => userPaused.delete(video));

    if (!wasAutoplay) return; // kullanici baslatacak videolar: sadece preload kapandi
    autoGated.add(video);
    if (observer) observer.observe(video);
    else start(video); // IntersectionObserver yoksa eski davranis
  }

  const sweep = (root) => (root || document).querySelectorAll('video').forEach(gate);

  sweep();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sweep(), { once: true });
  }
  // Bolum/varyant yeniden render'larinda (Shopify Section Rendering API) yeni videolar gelir.
  new MutationObserver((muts) => {
    for (const m of muts) {
      for (const n of m.addedNodes) {
        if (n.nodeType !== 1) continue;
        if (n.tagName === 'VIDEO') gate(n);
        else if (n.querySelectorAll) sweep(n);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  // --- Orijinal davranis korunur: iOS dusuk-guc modunda dokunma/tik ile devam ettir ---
  if (!customElements.get('low-power-video')) {
    customElements.define('low-power-video', class LowPowerVideo extends HTMLElement {
      connectedCallback() {
        this.video_section = this.querySelector('.wt-video');
        this.video_background_section = this.querySelector('.hero--video-background');
        const host = this.video_section || this.video_background_section || this;
        const video = host.querySelector('video') || this.querySelector('video');
        if (!video) return;
        this.video = video;
        gate(video);
        this.trigger = this.video_background_section
          ? this.video_background_section.querySelector('.hero__overlay') || this.video_background_section
          : host;
        this.resume = () => { if (video.paused) { userPaused.delete(video); start(video); } };
        this.trigger.addEventListener('touchstart', this.resume, { passive: true });
        this.trigger.addEventListener('click', this.resume);
      }

      disconnectedCallback() {
        if (this.trigger && this.resume) {
          this.trigger.removeEventListener('touchstart', this.resume);
          this.trigger.removeEventListener('click', this.resume);
        }
        if (observer && this.video) observer.unobserve(this.video);
      }
    });
  }
})();
